import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Autocomplete,
  Grid,
  CircularProgress,
} from '@mui/material';
import { Guideline, guidelinesApi, organisationsApi } from '../../../../api';

interface AddGuidelineDialogProps {
  open: boolean;
  organisationId: string;
  existingGuidelineIds: string[];
  onClose: () => void;
  onSuccess: () => void;
}

const AddGuidelineDialog: React.FC<AddGuidelineDialogProps> = ({
  open,
  organisationId,
  existingGuidelineIds,
  onClose,
  onSuccess,
}) => {
  const [guidelines, setGuidelines] = useState<Guideline[]>([]);
  const [loadingGuidelines, setLoadingGuidelines] = useState(false);
  const [selectedGuideline, setSelectedGuideline] = useState<Guideline | null>(null);
  const [formData, setFormData] = useState({
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchAvailableGuidelines();
    }
  }, [open]);

  const fetchAvailableGuidelines = async () => {
    setLoadingGuidelines(true);
    try {
      const response = await guidelinesApi.list({ limit: 1000 });
      // Filter out already assigned guidelines
      const available = response.data.items.filter(
        g => !existingGuidelineIds.includes(g.guideline_docs_id)
      );
      setGuidelines(available);
    } catch (err) {
      console.error('Failed to fetch guidelines:', err);
    } finally {
      setLoadingGuidelines(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedGuideline) {
      setError('Please select a guideline');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await organisationsApi.addGuideline(organisationId, {
        guideline_docs_id: selectedGuideline.guideline_docs_id,
        guideline_title: selectedGuideline.title,
        guideline_description: selectedGuideline.description,
        ...formData,
      });
      onSuccess();
    } catch (err: any) {
      console.error('Failed to add guideline:', err);
      setError(err.response?.data?.detail || 'Failed to add guideline to organisation');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedGuideline(null);
    setFormData({ notes: '' });
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Guideline to Organisation</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={guidelines}
              getOptionLabel={(option) => option.title}
              loading={loadingGuidelines}
              value={selectedGuideline}
              onChange={(_, value) => setSelectedGuideline(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Guideline"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingGuidelines ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.guideline_docs_id}>
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      {option.title}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'gray' }}>
                      {option.source_org} • {option.version_label}
                    </div>
                  </div>
                </li>
              )}
            />
          </Grid>

          

          

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              placeholder="Optional notes about this guideline assignment..."
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading} variant="ghost">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !selectedGuideline}>
          {loading ? 'Adding...' : 'Add Guideline'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddGuidelineDialog;
